import {TextEditorServices} from '@frontapp/editor-html';
import React, {FC} from 'react';

import {ComposerAiFeature} from '../../../../../../libs/shared/core/src/helpers/ai/composeV2Helpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {getHasAiDisableComposeWhenTooShort} from '../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {
  ComposerPanelId,
  ViewerPanelId,
} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ToolbarButton} from '../../../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {openInBrowser} from '../../../../../../srcCommon/browser/openInBrowser';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {helpCenterUrl} from '../../../../../store/stateless/navigationActions';
import {AiComposeGenerateDropdown} from '../dropdowns/aiComposeGenerateDropdown';
import {AiComposeRefineDropdown} from '../dropdowns/aiComposeRefineDropdown';

/*
 * Types.
 */

export interface AiComposeToolbarButtonProps {
  panelId: ComposerPanelId | ViewerPanelId;
  isDisabled: boolean;
  isTextSelected: boolean;
  editorServices: TextEditorServices;
  onAiFeature: (feature: ComposerAiFeature) => void;
  onAiMenuOpen?: () => void;
}

/*
 * Component.
 */

export const AiComposeToolbarButton: FC<AiComposeToolbarButtonProps> = (props) => {
  const openLmsArticle = () => {
    // "AI Compose and Summarize features"
    openInBrowser(`${helpCenterUrl}/en/articles/2003`);
  };

  const onGenerateDropdownClickLearnMore = () => {
    openLmsArticle();
  };

  const shouldDisableWhenTextTooShort = useAppSelector(getHasAiDisableComposeWhenTooShort);

  const {text} = props.editorServices.text;
  const {isEmpty} = props.editorServices.empty;

  const isTextTooShort = shouldDisableWhenTextTooShort ? Boolean((text && text.length < 5) || !text) : false;

  const renderButton = (isOpen: boolean) => (
    <UserThemeConsumer>
      {(theme) => (
        <ToolbarButton
          interactionId="ai-compose-v2-toolbar-button"
          hasHover={isOpen}
          iconName="sparklesAi"
          label={
            props.isDisabled ? (
              <Trans id="ai-compose-toolbar-button-ai-disabled">
                Your administrator has disabled this feature
              </Trans>
            ) : (
              <Trans id="ai-compose-toolbar-button-compose-with-ai">Compose with AI</Trans>
            )
          }
          color={theme.greys.shade80}
          hoverColor={theme.greys.shade80}
          isDisabled={props.isDisabled}
        />
      )}
    </UserThemeConsumer>
  );

  const renderModal = (modalProps: PopoverTransferProps) => {
    if (isEmpty || isTextTooShort) {
      return (
        <AiComposeGenerateDropdown
          popoverTransferProps={modalProps}
          onAiFeature={props.onAiFeature}
          onClickLearnMore={onGenerateDropdownClickLearnMore}
          isTextTooShort={isTextTooShort}
        />
      );
    }

    return (
      <AiComposeRefineDropdown
        isExclusive
        popoverTransferProps={modalProps}
        excludeFeatures={[]}
        onAiFeature={(feature) => {
          props.onAiFeature(feature);
          modalProps.onRequestClose();
        }}
      />
    );
  };

  return (
    <PopoverCoordinator
      isDisabled={props.isDisabled}
      renderButton={renderButton}
      renderModal={renderModal}
      onPopoverToggle={(isOpen) => {
        if (isOpen) {
          props.onAiMenuOpen?.();
        }
      }}
    />
  );
};
